import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Select } from "@/shared/components/ui/select";
import {
  Organization,
  OrganizationService,
  UpdateOrganizationPayload,
} from "@/shared/services/organization.service";
import { TariffService, Tariff } from "@/shared/services/tariff.service";
import { format } from "date-fns";
import { toast } from "sonner";
import { Checkbox } from "@/shared/components/ui/checkbox";

// Form schema with validation
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters." }),
  description: z.string().optional(),
  tariff_id: z.string({ required_error: "Please select a tariff." }),
  next_payment_date: z.string(),
  is_active: z.boolean(),
  is_internal: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface EditOrganizationModalProps {
  organization: Organization | null;
  isOpen: boolean;
  onClose: () => void;
  onOrganizationUpdated: () => void;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  organization,
  isOpen,
  onClose,
  onOrganizationUpdated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      tariff_id: "",
      next_payment_date: "",
      is_active: true,
      is_internal: false,
    },
  });

  // Load tariffs and set form values when modal opens
  useEffect(() => {
    if (isOpen && organization) {
      const fetchTariffs = async () => {
        try {
          const tariffsData = await TariffService.getAll();
          setTariffs(tariffsData);

          // Format the date for the input field
          const paymentDate = new Date(organization.next_payment_date);
          const formattedDate = format(paymentDate, "yyyy-MM-dd");

          // Set form values
          form.reset({
            name: organization.name,
            description: organization.description || "",
            tariff_id: organization.tariff ? organization.tariff.id || "" : "",
            next_payment_date: formattedDate,
            is_active: organization.is_active,
            is_internal: organization.is_internal,
          });
        } catch (err) {
          setError("Failed to load data");
          console.error("Error loading data:", err);
        }
      };
      fetchTariffs();
    }
  }, [isOpen, organization, form]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
      setError(null);
    }
  }, [isOpen, form]);

  const onSubmit = async (data: FormValues) => {
    if (!organization) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: UpdateOrganizationPayload = {
        name: data.name,
        description: data.description || "",
        tariff_id: data.tariff_id,
        next_payment_date: data.next_payment_date,
        is_active: data.is_active,
        is_internal: data.is_internal,
      };

      await OrganizationService.update(organization.id, payload);
      toast.success("Organization updated successfully");
      onClose();
      onOrganizationUpdated();
    } catch (err) {
      setError("Failed to update organization");
      toast.error("Failed to update organization");
      console.error("Error updating organization:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Organization</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter description (optional)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tariff_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Tariff</FormLabel>
                  <FormControl>
                    <Select {...field}>
                      <option value="" disabled>
                        Select a tariff
                      </option>
                      {tariffs.map((tariff) => (
                        <option key={tariff._id} value={tariff._id}>
                          {tariff.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="next_payment_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Next Payment Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_internal"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Internal Organization</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Check this if this is an internal organization
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Organization"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrganizationModal;
